import { useMemo } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import { usePatchLoanLiabilityLinesMutation } from '@/apis/extended/loanApplicationApi';
import type {
  LoanApplication,
  LoanLiabilityLine,
} from '@/types/LoanApplicationType';
import { parseLoanPatchError } from './loanSectionForm';
import { LoanSectionFormFooter } from './LoanSectionFormFooter';
import { useLoanSectionForm } from './useLoanSectionForm';

type Props = {
  application: LoanApplication;
  readOnly?: boolean;
};

const emptyRow = (): LoanLiabilityLine => ({
  creditorName: '',
  accountType: '',
  monthlyPayment: null,
  unpaidBalance: null,
  toBePaidOff: false,
});

export function LiabilitiesSection({ application, readOnly }: Props) {
  const [patch, { isLoading }] = usePatchLoanLiabilityLinesMutation();

  const initialLines = useMemo(() => {
    const src = application.liabilityLines;
    return src?.length ? src.map((r) => ({ ...r })) : [emptyRow()];
  }, [application._id, application.liabilityLines, application.updatedAt]);

  const {
    draft: lines,
    setDraft: setLines,
    isDirty,
    markSaved,
    resetDraft,
    readOnly: formReadOnly,
  } = useLoanSectionForm({
    sectionKey: 'liabilities',
    applicationId: application._id,
    applicationUpdatedAt: application.updatedAt,
    initialValue: initialLines,
    readOnly,
  });

  const saveSection = async () => {
    if (formReadOnly) return;
    try {
      await patch({
        id: application._id,
        liabilityLines: lines,
      }).unwrap();
      markSaved(lines);
      toast.success('Saved');
    } catch (e: unknown) {
      toast.error(parseLoanPatchError(e));
    }
  };

  const disabled = formReadOnly || isLoading;

  return (
    <div>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Monthly obligations and revolving debt (URLA liabilities). List all that
        apply; use $0 or leave balance empty if none.
      </Typography>
      {lines.map((row, idx) => (
        <Grid container spacing={2} key={row._id ?? idx} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Creditor"
              value={row.creditorName}
              disabled={disabled}
              onChange={(e) => {
                const n = [...lines];
                n[idx] = { ...row, creditorName: e.target.value };
                setLines(n);
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Account type"
              value={row.accountType}
              disabled={disabled}
              onChange={(e) => {
                const n = [...lines];
                n[idx] = { ...row, accountType: e.target.value };
                setLines(n);
              }}
              placeholder="e.g. Auto loan, Card, Student"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Monthly payment"
              type="number"
              value={row.monthlyPayment ?? ''}
              disabled={disabled}
              onChange={(e) => {
                const n = [...lines];
                n[idx] = {
                  ...row,
                  monthlyPayment:
                    e.target.value === '' ? null : Number(e.target.value),
                };
                setLines(n);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Unpaid balance"
              type="number"
              value={row.unpaidBalance ?? ''}
              disabled={disabled}
              onChange={(e) => {
                const n = [...lines];
                n[idx] = {
                  ...row,
                  unpaidBalance:
                    e.target.value === '' ? null : Number(e.target.value),
                };
                setLines(n);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={1}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={row.toBePaidOff}
                  disabled={disabled}
                  onChange={(_, v) => {
                    const n = [...lines];
                    n[idx] = { ...row, toBePaidOff: v };
                    setLines(n);
                  }}
                />
              }
              label="Pay off"
            />
          </Grid>
          {!formReadOnly && (
            <Grid item xs={12}>
              <IconButton
                onClick={() => setLines((R) => R.filter((_, i) => i !== idx))}
              >
                <i className="fa-light fa-trash" />
              </IconButton>
            </Grid>
          )}
        </Grid>
      ))}
      {!formReadOnly && (
        <Box sx={{ mb: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setLines((R) => [...R, emptyRow()])}
          >
            Add liability
          </Button>
        </Box>
      )}
      <LoanSectionFormFooter
        readOnly={formReadOnly}
        isDirty={isDirty}
        isSaving={isLoading}
        saveLabel="Save liabilities"
        onSave={() => void saveSection()}
        onCancel={resetDraft}
      />
    </div>
  );
}
